import * as THREE from 'three';
import Behavior from './Behavior.js';
import Space from '../Components/Space.js';
import Card from '../Components/Card.js';
import { supportsTouchInput } from '../Systems/FocusToggle.js';

/**
 * Sandbox behavior — the original freeform card table experience.
 *
 * Cards can be selected, dragged, flipped, focused, and highlifted.
 * This extracts all game-logic that was previously hardcoded in Space.js
 * and Interaction.js.
 */
export default class SandboxBehavior extends Behavior {

    onSetup(cards) {
        // Sandbox needs no special setup — cards are placed by Space.onResourcesLoaded.
    }

    onDestroy() {}

    canUseClickMove() { return false; }
    canUsePinchFocus() { return true; }

    // ─── Input hooks ──────────────────────────────────────────────────

    onTap(card, position) {
        if (!this.canUseClickMove()) return;

        const space = this.space;

        if (!card) {
            this.interaction.lastDroppedCard = null;
            space.selectNothing();
            this._toggleHighLiftedCard(null);
            this.interaction.syncFocusToggleStateFromSpace();
            return;
        }

        if (space.focusedCard) return;

        this.interaction.lastDroppedCard = null;
        this._toggleSelectedCard(card);
    }

    onDoubleTap(card, position) {
        const space = this.space;

        if (space.focusedCard) {
            space.defocusAnyCard();
            this.interaction.syncFocusToggleStateFromSpace();
            return;
        }

        this.interaction.lastDroppedCard = null;

        if (card) {
            if (space.highLiftedCard && space.highLiftedCard.id !== card.id) {
                this._toggleHighLiftedCard(card);
            }
            space.flipCard(card);
        } else {
            this._toggleHighLiftedCard(null);
        }
        this.interaction.syncFocusToggleStateFromSpace();
    }

    onPanStart(card, position) {
        const space = this.space;

        if (space.focusedCard) return false;

        // 2D pan: drag on background moves camera (mobile only)
        if (!card && supportsTouchInput()) {
            this.interaction.lastDroppedCard = null;
            this.interaction.syncFocusToggleStateFromSpace();
            return 'camera_pan';
        }

        if (!card) return false;

        this.interaction.lastDroppedCard = null;

        if (card.isHighLifted) {
            card.isHighLifted = false;
            space.highLiftedCard = null;
            this._toggleSelectedCard(card);
        }
        if (space.selectedCard?.id !== card.id) this._toggleSelectedCard(card);

        return true;
    }

    onPanMove(card, position) {
        const space = this.space;
        if (space.focusedCard) return;

        const selectedCard = space.selectedCard;
        if (!selectedCard) return;

        const tablePos = space.intersectTablePos(position);
        if (!tablePos) return;

        tablePos.x += this.interaction.dragOffset.x;
        tablePos.y += this.interaction.dragOffset.y;
        space.dragCard(selectedCard, tablePos);
    }

    onPanEnd(card, position) {
        const space = this.space;
        if (space.focusedCard) return;

        const selectedCard = space.selectedCard;
        if (!selectedCard) return;

        const hoveredCard = space.intersectCard(position);
        this.interaction.dragOffset.set(0, 0);
        space.deselectCard(selectedCard);

        if (hoveredCard) {
            space.activateHighlightCard(hoveredCard);
            space.lastMousePos = position;
            this.interaction.lastScrollDirection = null;
        } else {
            space.removeHighlightCard();
        }

        this.interaction.setLastDroppedCard(selectedCard);
        this.interaction.syncFocusToggleStateFromSpace();
        this.interaction.InputIsDrag = false;
        space.eventBus.pushLocal('sandboxCardDragged', { id: selectedCard.id });
    }

    onMouseMove(card, position) {
        const space = this.space;

        if (space.focusedCard) {
            space.focusedCard.tiltTowards(position.x, position.y);
            return;
        }

        if (!card) {
            space.removeHighlightCard();
        } else {
            space.activateHighlightCard(card);
            space.lastMousePos = position;
            this.interaction.lastScrollDirection = null;
        }

        if (!this.canUseClickMove()) return;
        if (supportsTouchInput() && !space.highLiftedCard) return;
        if (space.selectedCard && !this.interaction.InputIsDrag) {
            const tablePos = space.intersectTablePos(position);
            space.dragCard(space.selectedCard, tablePos);
        }
    }

    onScrollWheel(card, direction) {
        const space = this.space;

        if (space.scrollBusy) return;

        const target = space.focusedCard || space.highlightedCard || space.selectedCard || space.highLiftedCard;
        if (!target) return;

        if (!space.focusedCard) {
            space.focusDir = direction;
            space.scrollBusy = true;
            space.focusCard(target, () => { space.scrollBusy = false; });
        } else if (direction !== space.focusDir) {
            space.scrollBusy = true;
            space.defocusAnyCard(() => {
                space.scrollBusy = false;
                space.focusDir = null;
                const cardIntersect = space.intersectCard(space.lastMousePos);
                space.activateHighlightCard(cardIntersect);
            });
        }
    }

    onKeyDown(card, event) {
        const space = this.space;
        const selectedCard = space.selectedCard;
        const focusedCard = space.focusedCard;
        const highlightedCard = space.highlightedCard;

        switch (event.code) {
            case 'KeyF':
                if (focusedCard) {
                    space.defocusAnyCard();
                    return;
                }
                if (selectedCard || highlightedCard) {
                    space.focusCard(selectedCard || highlightedCard);
                }
                break;
            case 'Space': {
                const flipTarget = selectedCard || focusedCard || highlightedCard;
                if (flipTarget) {
                    this.interaction.spaceHeld = true;
                    this.interaction.stopSpaceFlipLoop();
                    this.interaction.performSpaceFlip();
                }
                break;
            }
        }
    }

    onKeyUp(event) {
        if (event.code === 'Space') {
            this.interaction.spaceHeld = false;
            this.interaction.stopSpaceFlipLoop();
        }
    }

    onWindowBlur() {
        this.interaction.spaceHeld = false;
        this.interaction.stopSpaceFlipLoop();
    }

    // ─── Remote event handlers ────────────────────────────────────────

    getRemoteHandlers() {
        const space = this.space;
        return {
            draggedCard: (data) => {
                const card = space.getCard(data.id);
                if (!card) return;
                if (space.selectedCard?.id === card.id) {
                    space.deselectCard(card);
                }
                card.liftUp(Space.CARD_LIFTED_REMOTE_Z);
                card.dragTo(new THREE.Vector2(data.x, data.y), Card.ROTATION_MAX * Space.CARD_LIFTED_REMOTE_ROTATION_FACTOR);
            },
            selectedCard: (data) => {
                const card = space.getCard(data.id);
                if (!card) return;
                if (space.selectedCard?.id === card.id) {
                    space.selectedCard.deselect();
                    space.selectedCard = null;
                    space.emitCardStateChanged();
                }
                card.liftUp(Space.CARD_LIFTED_REMOTE_Z);
            },
            deselectedCard: (data) => {
                const card = space.getCard(data.id);
                if (!card) return;
                if (space.selectedCard?.id === card.id) {
                    space.reorderCards(space.selectedCard);
                    space.selectedCard.deselect();
                    space.selectedCard = null;
                    space.emitCardStateChanged();
                } else {
                    space.reorderCards(card);
                    card.dropDown(Space.CARD_DOWN_Z);
                }
            },
            focusedCard: (data) => {
                const card = space.getCard(data.id);
                if (!card) return;
                if (space.focusedCard?.id === card.id) return;
                if (space.focusedCard && space.focusedCard.id !== card.id) {
                    space.focusedCard.defocus();
                }
                if (space.selectedCard) {
                    space.selectedCard.deselect();
                    space.reorderCards(space.selectedCard);
                    space.selectedCard = null;
                }
                card.focus();
                space.focusedCard = card;
                space.reorderCards(card);
                space.emitCardStateChanged();
            },
            defocusedCard: (data) => {
                const card = space.getCard(data.id);
                if (!card) return;
                if (space.focusedCard) space.focusedCard.defocus();
                space.focusedCard = null;
                if (space.selectedCard) space.selectedCard.deselect();
                space.selectedCard = null;
                space.reorderCards(card);
                space.emitCardStateChanged();
            },
            flippedCardUp: (data) => {
                const card = space.getCard(data.id);
                if (!card) return;
                space.reorderCards(card);
                card.faceUp();
            },
            flippedCardDown: (data) => {
                const card = space.getCard(data.id);
                if (!card) return;
                space.reorderCards(card);
                card.faceDown();
            },
        };
    }

    // ─── Private sandbox logic ────────────────────────────────────────

    _toggleSelectedCard(cardOrId) {
        const space = this.space;
        const card = space.getCard(cardOrId);

        if (space.selectedCard && space.selectedCard.id !== card.id) {
            space.deselectCard(space.selectedCard);
        }

        if (card.isSelected) {
            space.deselectCard(card);
        } else {
            if (space.highLiftedCard?.id === card.id) {
                this._toggleHighLiftedCard(card);
            } else {
                this._toggleHighLiftedCard(card);
                space.selectCard(card);
                space.removeHighlightCard();
            }
        }
    }

    _toggleHighLiftedCard(cardOrId) {
        const space = this.space;

        if (!cardOrId) {
            if (space.highLiftedCard) {
                space.highLiftedCard.dropDown(Space.CARD_DOWN_Z);
                space.eventBus.pushRemote('deselectedCard', { id: space.highLiftedCard.id, index: space.highLiftedCard.sortOrder });
                space.highLiftedCard = null;
                space.emitCardStateChanged();
            }
            return;
        }

        const card = space.getCard(cardOrId);

        if (space.highLiftedCard && space.highLiftedCard.id !== card.id) {
            space.highLiftedCard.dropDown(Space.CARD_DOWN_Z);
            space.eventBus.pushRemote('deselectedCard', { id: space.highLiftedCard.id, index: space.highLiftedCard.sortOrder });
            space.highLiftedCard = null;
            space.emitCardStateChanged();
        }

        if (space.highLiftedCard?.id === card.id) {
            card.dropDown(Space.CARD_DOWN_Z);
            space.highLiftedCard = null;
            space.eventBus.pushRemote('deselectedCard', { id: card.id, index: card.sortOrder });
            space.emitCardStateChanged();
        }
    }
}
